import React, { useRef } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAppLocale } from '../../providers/LocaleProvider';

/** Passed row shown when a lesson, tag lesson, or tag item is passed. */
export const PassedLessonBanner: React.FC<{ label: string }> = ({ label }) => {
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const tickLottieRef = useRef<{ stop: () => void; play: () => void } | null>(null);

  const replay = () => {
    const player = tickLottieRef.current;
    if (player) {
      player.stop();
      player.play();
    }
  };

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className={
        isRtl
          ? 'relative flex items-center gap-3 overflow-visible rounded-lg border border-blue-200 bg-blue-50 py-2 pl-3 pr-11 dark:border-blue-800 dark:bg-blue-900/20 sm:pr-14'
          : 'relative flex items-center gap-3 overflow-visible rounded-lg border border-blue-200 bg-blue-50 py-2 pl-11 pr-3 dark:border-blue-800 dark:bg-blue-900/20 sm:pl-14'
      }
    >
      <div
        className={
          isRtl
            ? 'absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer select-none items-center justify-center overflow-visible sm:h-14 sm:w-14'
            : 'absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer select-none items-center justify-center overflow-visible sm:h-14 sm:w-14'
        }
        style={isRtl ? { marginRight: '-4px' } : { marginLeft: '-4px' }}
        role="button"
        tabIndex={0}
        aria-label="Replay passed animation"
        onClick={replay}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            replay();
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          replay();
        }}
      >
        <DotLottieReact
          src="/animations/Passed.lottie"
          loop={false}
          autoplay
          className="h-full w-full"
          dotLottieRefCallback={(instance: { stop: () => void; play: () => void } | null) => {
            tickLottieRef.current = instance;
          }}
        />
      </div>
      <span className="inline-flex items-center rounded-md border border-green-200 bg-green-100 px-2.5 py-0.5 text-sm font-medium text-green-800 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-200">
        {label}
      </span>
    </div>
  );
};
